import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STORAGE_KEY = "alta_prereg_done";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mono text-[10px] uppercase tracking-[0.28em] text-[#F1EDE6]/55">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Success({ t, onWatch, onAgain }) {
  return (
    <div data-testid="prereg-success" className="border border-[color:var(--alta-red)]/50 bg-[color:var(--alta-red)]/10 p-8 md:p-12 fade-up">
      <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--alta-red-soft)]">
        <span className="rec-dot" /> {t.s_eyebrow}
      </div>
      <h3 className="mt-5 condensed text-[56px] md:text-[80px] text-[#F1EDE6]">{t.s_title}</h3>
      <p className="mt-4 text-[15px] leading-[1.75] text-[#F1EDE6]/75 font-light max-w-[58ch]">{t.s_body}</p>
      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <button type="button" onClick={onWatch} data-testid="prereg-success-watch" className="btn-red-primary">{t.s_cta}</button>
        <button type="button" onClick={onAgain} data-testid="prereg-success-again" className="btn-outline-light">{t.s_again}</button>
      </div>
    </div>
  );
}

export default function PreRegisterForm({ t, onWatch }) {
  const [done, setDone] = useState(() => !!localStorage.getItem(STORAGE_KEY));
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ full_name: "", instagram: "", whatsapp: "", city: "", followers_range: "", why: "", consent: false });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const options = String(t.f_followers_opts || "").split("|").map((s) => s.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return;
    setLoading(true);
    try {
      const res = await api.post("/reality/pre-registration", f);
      localStorage.setItem(STORAGE_KEY, res.data.instagram || "1");
      setDone(true);
      toast.success(res.data.already ? "Inscrição atualizada." : "Você está na pré-lista.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Success t={t} onWatch={onWatch} onAgain={() => setDone(false)} />;

  return (
    <form onSubmit={submit} data-testid="prereg-form" className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
        <Field label={t.f_name}>
          <input data-testid="prereg-name" className="field-dark" value={f.full_name} onChange={set("full_name")} placeholder={t.f_name_ph} required minLength={2} />
        </Field>
        <Field label={t.f_instagram}>
          <input data-testid="prereg-instagram" className="field-dark" value={f.instagram} onChange={set("instagram")} placeholder={t.f_instagram_ph} required minLength={2} />
        </Field>
        <Field label={t.f_whatsapp}>
          <input data-testid="prereg-whatsapp" className="field-dark" value={f.whatsapp} onChange={set("whatsapp")} placeholder={t.f_whatsapp_ph} />
        </Field>
        <Field label={t.f_city}>
          <input data-testid="prereg-city" className="field-dark" value={f.city} onChange={set("city")} placeholder={t.f_city_ph} />
        </Field>
        <Field label={t.f_followers}>
          <select data-testid="prereg-followers" className="field-dark" value={f.followers_range} onChange={set("followers_range")}>
            <option value="">—</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label={t.f_why}>
            <textarea data-testid="prereg-why" className="field-dark resize-none" rows={2} value={f.why} onChange={set("why")} placeholder={t.f_why_ph} maxLength={800} />
          </Field>
        </div>
      </div>

      <label className="flex items-start gap-4 cursor-pointer select-none" data-testid="prereg-consent-label">
        <input type="checkbox" data-testid="prereg-consent" checked={f.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-[color:var(--alta-red)]" />
        <span className="text-[13px] leading-[1.6] text-[#F1EDE6]/70 font-light">{t.f_consent}</span>
      </label>

      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <button type="submit" disabled={!f.consent || loading} data-testid="prereg-submit" className="btn-red-primary">
          {loading ? t.f_submit_loading : t.f_submit}
        </button>
        <span className="mono text-[9px] uppercase tracking-[0.26em] text-[#F1EDE6]/45">{t.f_privacy}</span>
      </div>
    </form>
  );
}
